import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'jarvis_completed_tours';

export interface OnboardingContextType {
  // 状態
  isOnboardingActive: boolean;
  currentTourKey: string | null;
  completedTours: Set<string>;

  // アクション
  startTour: (tourKey: string) => void;
  completeTour: (tourKey: string) => Promise<void>;
  skipAllTours: () => Promise<void>;
  resetTour: (tourKey: string) => void;

  // チェック
  isTourCompleted: (tourKey: string) => boolean;
  shouldShowTour: (tourKey: string) => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user, markOnboardingComplete } = useAuth();
  const [currentTourKey, setCurrentTourKey] = useState<string | null>(null);
  const [completedTours, setCompletedTours] = useState<Set<string>>(new Set());
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  // LocalStorageから完了済みツアーを読み込み
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const tourList = JSON.parse(stored) as string[];
        setCompletedTours(new Set(tourList));
      }
    } catch (error) {
      console.error('Error loading completed tours from localStorage:', error);
    }
  }, []);

  // LocalStorageに完了済みツアーを保存
  const saveCompletedTours = useCallback((tours: Set<string>) => {
    try {
      const tourList = Array.from(tours);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tourList));
    } catch (error) {
      console.error('Error saving completed tours to localStorage:', error);
    }
  }, []);

  // ツアー開始
  const startTour = useCallback((tourKey: string) => {
    setCurrentTourKey(tourKey);
    setIsOnboardingActive(true);
  }, []);

  // ツアー完了
  const completeTour = useCallback(async (tourKey: string) => {
    try {
      // 完了済みリストに追加
      const updatedTours = new Set(completedTours);
      updatedTours.add(tourKey);
      setCompletedTours(updatedTours);
      saveCompletedTours(updatedTours);

      // 現在のツアーをクリア
      if (currentTourKey === tourKey) {
        setCurrentTourKey(null);
        setIsOnboardingActive(false);
      }

      // Supabaseのオンボーディング状態を更新
      await markOnboardingComplete();
    } catch (error) {
      console.error('Error completing tour:', error);
      throw error;
    }
  }, [completedTours, currentTourKey, markOnboardingComplete, saveCompletedTours]);

  // すべてのツアーをスキップ
  const skipAllTours = useCallback(async () => {
    try {
      setCurrentTourKey(null);
      setIsOnboardingActive(false);

      // Supabaseのオンボーディング状態を更新
      await markOnboardingComplete();
    } catch (error) {
      console.error('Error skipping all tours:', error);
      throw error;
    }
  }, [markOnboardingComplete]);

  // ツアーをリセット（開発/デバッグ用）
  const resetTour = useCallback((tourKey: string) => {
    const updatedTours = new Set(completedTours);
    updatedTours.delete(tourKey);
    setCompletedTours(updatedTours);
    saveCompletedTours(updatedTours);

    if (currentTourKey === tourKey) {
      setCurrentTourKey(null);
      setIsOnboardingActive(false);
    }
  }, [completedTours, currentTourKey, saveCompletedTours]);

  // ツアーが完了済みかチェック
  const isTourCompleted = useCallback((tourKey: string): boolean => {
    return completedTours.has(tourKey);
  }, [completedTours]);

  // ツアーを表示すべきかチェック
  const shouldShowTour = useCallback((tourKey: string): boolean => {
    // ユーザーがログインしていない場合は表示しない
    if (!user) {
      return false;
    }

    // すでにオンボーディングが完了している場合は表示しない
    if (user.hasCompletedOnboarding) {
      return false;
    }

    // 初回ログインでない場合は表示しない
    if (!user.isFirstLogin) {
      return false;
    }

    // すでに完了済みの場合は表示しない
    if (completedTours.has(tourKey)) {
      return false;
    }

    return true;
  }, [user, completedTours]);

  const value: OnboardingContextType = {
    isOnboardingActive,
    currentTourKey,
    completedTours,
    startTour,
    completeTour,
    skipAllTours,
    resetTour,
    isTourCompleted,
    shouldShowTour,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboardingContext() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider');
  }
  return context;
}
